import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GuidedTourDirective } from '../../guided-tour.directive';
import { GuidedTourService } from '../../../service/guided-tour.service';
import { OA_ADMIN_AREA } from '../../../model/guided-tour';
import { GroupAssignmentComponent } from '@markant-platform/dashboard/feature-dashboard-group-assignment';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { SectionFinishDialogComponent } from '../../../section-finish-dialog/section-finish-dialog.component';
import { GuidedTourWrapperComponent } from '../../guided-tour-wrapper.component';
import { GuidedTourHighlightDialogComponent } from '../../highlight-dialog/guided-tour-highlight-dialog.component';
import { GroupAssignmentDemoComponent } from '../my-groups/group-assignment-demo.component';
import { MY_SERVICES_GUIDED_TOUR_STEPS, SERVICES_HIGHLIGHT_DIALOG_CONFIG } from './utils/my-services-guided-tour-steps';
import { Router } from '@angular/router';
import { ArrayIncludesPipe } from '@markant-platform/shared/util-pipes';
import { BookingsTableDemoComponent } from './bookings-table-demo/bookings-table-demo.component';
import { ManagementViewDemoComponent } from './management-view-demo/management-view-demo.component';
import { CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { BehaviorSubject, first, firstValueFrom, map } from 'rxjs';
import { Cartridge } from '@markant-platform/cartridge/model';
import { AppConfigService } from '@markant-platform/shared/data-access';
import { isOrgNewAndLvl3 } from '@markant-platform/shared/util-helpers';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';

@Component({
  selector: 'markant-platform-my-services',
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    GroupAssignmentComponent,
    SectionFinishDialogComponent,
    GuidedTourWrapperComponent,
    GuidedTourHighlightDialogComponent,
    GroupAssignmentDemoComponent,
    TranslocoModule,
    ArrayIncludesPipe,
    BookingsTableDemoComponent,
    ManagementViewDemoComponent,
  ],
  templateUrl: './my-services.component.html',
  styleUrl: './my-services.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyServicesComponent extends GuidedTourDirective implements OnInit {
  @ViewChild('guidedTourWrapper') guidedTourWrapper!: GuidedTourWrapperComponent;

  static TOUR_SECTION_NUMBER = 4;
  steps = MY_SERVICES_GUIDED_TOUR_STEPS;
  dialogConfig = SERVICES_HIGHLIGHT_DIALOG_CONFIG;
  enableClickThrough = true;
  selectedCartridgeId = '';
  selectedCartridgeGroupKey = '';
  showHighlightDialog$ = new BehaviorSubject(false);
  skipPriceRequestStep = false;

  readonly DOUBLE_BACK_STEPS = [4, 9];
  readonly STEPS_WITH_PREVIOUS_BUTTON = [10, 11, 12, 13];
  readonly STEPS_BOOKING_TABLE_DEMO = [9, 10, 11];
  readonly STEPS_MANAGER_VIEW_DEMO = [12, 13];

  // Previous and next button functions
  nextButtonCallback = this.nextTarget.bind(this);
  backButtonCallback = this.previousTarget.bind(this);

  private stepDelay?: number;

  constructor(
    protected override guidedTourService: GuidedTourService,
    protected override router: Router,
    private readonly cartridgeFacade: CartridgesFacade,
    private readonly cdRef: ChangeDetectorRef,
    private readonly appConfigService: AppConfigService,
    private readonly authUserService: AuthenticatedUserService,
    private readonly organizationService: OrganizationService,
    private readonly translocoService: TranslocoService
  ) {
    super(OA_ADMIN_AREA, MyServicesComponent.TOUR_SECTION_NUMBER, guidedTourService, router);
  }

  override async ngOnInit(): Promise<void> {
    this.getCartridgeDynamicIds().then(() => {
      super.ngOnInit();
    });

    const orgId = await firstValueFrom(this.authUserService.getOrganizationId());
    const organization = await firstValueFrom(this.organizationService.getOrganizationDetailsById(orgId));
    this.skipPriceRequestStep = !isOrgNewAndLvl3(organization);

    if (!isOrgNewAndLvl3(organization)) {
      const ecsText = this.translocoService.translate(
        'guidedTour.tours.guided-tour-oa-admin-area.section4.description.step5ecs'
      );
      this.steps = this.steps.map((step, index) => ({
        ...step,
        ...(index === 4 && { description: { ...step.description, text: ecsText } }),
      }));
    }
  }

  /**
   * Methods related to the tour progression for this section
   */

  protected override async cancelSection(): Promise<void> {
    await this.guidedTourWrapper.end();
    super.cancelSection();
  }

  /**
   * 1- Admin area - Focus on "my services" dashboard card.
   * 2- Focus on navbar service catalog header.
   * 3- Timeout in service catalogue view.
   * 4- Focus on card in service catalogue.
   * 5- Focus on Cartridge detail, first variant.
   *
   */
  protected override async nextTarget(): Promise<void> {
    super.nextTarget();
    this.enableClickThrough = true;

    switch (this.currentStep) {
      case 1:
        await this.navigateToAdminDashboard();
        await this.guidedTourWrapper.start();
        break;
      case 2:
        await this.guidedTourWrapper.next();
        break;
      case 3:
        await this.guidedTourWrapper.next();
        this.stepDelay = window.setTimeout(async () => await this.nextTarget(), 3000);
        break;
      case 4:
        this.cleanTimeoutAndFocus(this.stepDelay);
        await this.guidedTourWrapper.next();
        this.scrollToElement(`service-${this.selectedCartridgeId}`);
        break;
      case 5:
        await this.guidedTourWrapper.next();
        this.scrollToElement('cartridge-variant-0');
        break;
      case 6:
        await this.guidedTourWrapper.next();
        break;
      case 7:
        await this.navigateToAdminDashboard();
        await this.guidedTourWrapper.next();
        break;
      case 8:
        this.changeBackNavigationButtonVisibility(false);
        this.stepDelay = window.setTimeout(async () => await this.nextTarget(), 3000);
        await this.guidedTourWrapper.next();
        break;
      case 9:
        this.cleanTimeoutAndFocus(this.stepDelay);
        this.showHighlightDialog$.next(true);
        await this.guidedTourWrapper.next();
        this.changeBackNavigationButtonVisibility(true);
        break;
      case 10:
        if (this.skipPriceRequestStep) {
          this.currentStep++;
          await this.guidedTourWrapper.next();
        }
        await this.guidedTourWrapper.next();
        break;
      case 11:
      case 12:
      case 13:
        await this.guidedTourWrapper.next();
        break;
      case 14:
        await this.guidedTourWrapper.end();
        await this.guidedTourService.completeSection(this.guidedTour.id, this.sectionNumber, true);
        break;
      default:
        await this.router.navigate(['']);
        await this.cancelSection();
        break;
    }
    this.cdRef.markForCheck();
  }

  protected override async previousTarget(): Promise<void> {
    if (this.currentStep === 9) {
      this.showHighlightDialog$.next(false);
      await this.navigateToAdminDashboard();
    }

    if (this.currentStep === 11 && this.skipPriceRequestStep) {
      await this.jumpToTarget(this.currentStep - 2);
      return;
    }

    const isDoubleBackTarget = this.DOUBLE_BACK_STEPS.includes(this.currentStep);
    await this.jumpToTarget(this.currentStep - (isDoubleBackTarget ? 2 : 1));
  }

  protected override async jumpToTarget(target: number): Promise<void> {
    if (target < 1) {
      await this.router.navigate(['']);
      await this.cancelSection();
      return;
    }
    super.jumpToTarget(target);
    this.enableClickThrough = true;

    await this.handleBackwardsNavigation();
    await this.guidedTourWrapper.jumpTo(target - 1);
  }

  async redirectToDashboard(): Promise<void> {
    await this.guidedTourWrapper.end();
    await this.router.navigate(['']);
  }

  /**
   * Private methods
   */
  private async navigateToAdminDashboard(): Promise<void> {
    await this.router.navigate(['/organization']);
  }

  private async navigateToCatalog(): Promise<void> {
    await this.router.navigate(['/cartridge/catalog'], { fragment: this.selectedCartridgeGroupKey });
  }

  private async navigateToCartridgeDetail(): Promise<void> {
    await this.router.navigate([`/cartridge/catalog/${this.selectedCartridgeId}/detail`]);
  }

  private async handleBackwardsNavigation(): Promise<void> {
    switch (this.currentStep) {
      case 2:
        await this.navigateToAdminDashboard();
        break;
      case 4:
        await this.navigateToCatalog();
        this.scrollToElement(`service-${this.selectedCartridgeId}`);
        break;
      case 5:
        this.scrollToElement('cartridge-variant-0');
        break;
      case 6:
        await this.navigateToCartridgeDetail();
        break;
      default:
        break;
    }
  }

  private changeBackNavigationButtonVisibility(visible: boolean): void {
    const backButtonElement = <HTMLDivElement>document.querySelector('#elements-guided-tour-back-button');

    if (!backButtonElement) {
      return;
    }

    visible ? backButtonElement.classList.remove('hidden') : backButtonElement.classList.add('hidden');
  }

  private async getCartridgeDynamicIds(): Promise<void> {
    const appConfig = await this.appConfigService.fetchAppConfiguration();
    const serviceHighlightId = appConfig.guidedTour.orgAdminIntroduction.highlightedServiceId;

    this.cartridgeFacade.allActiveCartridges$
      .pipe(
        first(),
        map((cartridges: Cartridge[]) => cartridges.find(({ id }) => id === serviceHighlightId))
      )
      .subscribe(cartridge => {
        if (cartridge) {
          const { id, cartridgeGroupKey, variants } = cartridge;
          const [firstVariant] = variants;

          this.selectedCartridgeId = id;
          this.selectedCartridgeGroupKey = cartridgeGroupKey;

          const clonedSteps = [...this.steps];
          clonedSteps[3].highlightTargetId = `service-${this.selectedCartridgeId}`;
          clonedSteps[4].highlightTargetId = `cartridge-variant-highlight-${firstVariant.variantId}`;

          this.steps = clonedSteps;
        }
      });
  }

  private scrollToElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }), 50);
    }
  }
}
